import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { CheckCircle2, Loader2, XCircle } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { ApiError } from '@/lib/api'
import { authApi } from '@/features/auth/api'

const errMsg = (err: unknown, fallback = 'Verification failed.') => {
  if (err instanceof ApiError && err.body && typeof err.body === 'object' && 'error' in err.body) {
    const m = (err.body as { error: unknown }).error
    if (typeof m === 'string' && m) return m
  }
  if (err instanceof Error && err.message) return err.message
  return fallback
}

export function VerifyEmailPage() {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const [state, setState] = useState<'pending' | 'ok' | 'error'>('pending')
  const [message, setMessage] = useState('Verifying your email…')

  useEffect(() => {
    if (!token) {
      setState('error')
      setMessage('No verification token provided.')
      return
    }
    let cancelled = false
    authApi
      .verifyEmail(token)
      .then((r) => {
        if (cancelled) return
        setState('ok')
        setMessage(r.message ?? 'Email verified - you can now sign in.')
      })
      .catch((err) => {
        if (cancelled) return
        setState('error')
        setMessage(errMsg(err, 'Verification link is invalid or expired.'))
      })
    return () => {
      cancelled = true
    }
  }, [token])

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-center text-2xl">Email verification</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-center">
        {state === 'pending' ? (
          <Loader2 className="mx-auto size-10 animate-spin text-primary" />
        ) : state === 'ok' ? (
          <CheckCircle2 className="mx-auto size-10 text-success" />
        ) : (
          <XCircle className="mx-auto size-10 text-destructive" />
        )}
        <p className="text-sm">{message}</p>
        <Button asChild className="w-full">
          <Link to="/login">Continue to sign in</Link>
        </Button>
      </CardContent>
    </Card>
  )
}
